'use client';

/**
 * SeatsLicensesPanel — an organization's seats by license type.
 *
 * Every member of an org holds a license: full, view-only, field, volunteer,
 * member portal or API. The type is set from the member's role automatically;
 * an operator can change it (or set it back to follow the role) and suspend or
 * release a seat. Portal and API accounts never bill as seats.
 *
 * Seat prices can be set per type for this organization, along with how a
 * month's seats are measured (peak, average or end of month). Without prices,
 * every billable seat bills at the plan's per-user price.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Users as IdCard, Loader2, Save } from 'lucide-react';

interface Api {
  get: <T = any>(path: string) => Promise<T | null>;
  post: <T = any>(path: string, body: any) => Promise<T>;
  put: <T = any>(path: string, body: any) => Promise<T>;
}
interface LicenseType { code: string; name: string; billable: boolean }
interface Member {
  user_id: number; name: string | null; email: string | null; role: string;
  license_type: string; license_type_source: string | null; seat_status: string;
}
interface Detail {
  counts: Record<string, number>; billable_seats: number; users: Member[];
  snapshots: { month: string; license_type: string; peak: number; average: number; end_of_month: number }[];
  license_types: LicenseType[];
  pricing: { plan: string | null; per_user_price?: number; effective_prices?: Record<string, number>; org_prices?: Record<string, number>; measure?: string; commitments?: Record<string, number> };
  over_commitment?: Record<string, number>;
}

const MEASURES = [
  { value: 'peak', label: 'Peak in the month' },
  { value: 'average', label: 'Daily average' },
  { value: 'end_of_month', label: 'End of month' },
];

export default function SeatsLicensesPanel({ cboId, api }: { cboId: number; api: Api }) {
  const [d, setD] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [measure, setMeasure] = useState('peak');
  const [commits, setCommits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get<Detail>(`/licenses/org/${cboId}`);
    setD(r);
    if (r) {
      const org = r.pricing.org_prices || {};
      setPrices(Object.fromEntries(Object.entries(org).map(([k, v]) => [k, String(v)])));
      setMeasure(r.pricing.measure || 'peak');
      setCommits(Object.fromEntries(Object.entries(r.pricing.commitments || {}).map(([k, v]) => [k, String(v)])));
    }
    setLoading(false);
  }, [api, cboId]);
  useEffect(() => { setNotice(null); setError(null); load(); }, [load]);

  const savePricing = async () => {
    setBusy('pricing'); setError(null);
    try {
      const license_prices = Object.fromEntries(Object.entries(prices).filter(([, v]) => v !== '').map(([k, v]) => [k, Number(v)]));
      await api.put(`/licenses/org/${cboId}/pricing`, { license_prices, measure });
      setNotice('Saved seat prices.');
      await load();
    } catch (e: any) { setError(e.message); }
    finally { setBusy(null); }
  };
  const saveCommitments = async () => {
    setBusy('commits'); setError(null);
    try {
      const commitments = Object.fromEntries(Object.entries(commits).filter(([, v]) => v !== '' && Number(v) > 0).map(([k, v]) => [k, Number(v)]));
      await api.put(`/licenses/org/${cboId}/commitments`, { commitments });
      setNotice('Saved seat commitments.');
      await load();
    } catch (e: any) { setError(e.message); }
    finally { setBusy(null); }
  };
  const setMember = async (m: Member, body: any) => {
    setBusy(`u:${m.user_id}`); setError(null);
    try {
      await api.put(`/licenses/org/${cboId}/users/${m.user_id}`, body);
      await load();
    } catch (e: any) { setError(e.message); }
    finally { setBusy(null); }
  };

  if (loading && !d) {
    return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading seats…</div>;
  }
  if (!d) return null;

  const types = d.license_types || [];
  const name = (code: string) => types.find((t) => t.code === code)?.name || code;
  const billableTypes = types.filter((t) => t.billable);
  const latestMonth = d.snapshots[0]?.month;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2"><IdCard className="h-4 w-4" /> Seats &amp; licenses</h3>
        <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">
          Each member's license is set from their role; change it here if needed. Member-portal and API accounts never bill as seats.
          {d.pricing.plan ? '' : ' This organization has no active plan.'}
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {notice && !error && <p className="text-sm text-green-700">{notice}</p>}

      <div className="flex flex-wrap gap-2">
        {types.map((t) => (
          <div key={t.code} className={`rounded-lg px-3 py-2 text-sm ${t.billable ? 'bg-gray-50 dark:bg-gray-900' : 'bg-gray-50/50 text-gray-400 dark:bg-gray-900/50'}`}>
            <div className="text-xs text-gray-500">{t.name}</div>
            <div className="text-lg font-semibold tabular-nums">{d.counts[t.code] || 0}</div>
          </div>
        ))}
        <div className="rounded-lg bg-indigo-50 px-3 py-2 text-sm dark:bg-indigo-900/30">
          <div className="text-xs text-indigo-700 dark:text-indigo-300">Billable seats</div>
          <div className="text-lg font-semibold tabular-nums text-indigo-800 dark:text-indigo-200">{d.billable_seats}</div>
        </div>
      </div>

      {d.pricing.plan && (
        <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 space-y-2">
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">Seat prices for this organization</div>
          <p className="text-xs text-gray-500">
            Leave a type blank to use the plan's price{d.pricing.per_user_price ? ` ($${d.pricing.per_user_price}/user)` : ''}. With no prices set, every billable seat bills at the plan's per-user price.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            {billableTypes.map((t) => (
              <label key={t.code} className="text-xs">
                <span className="block text-gray-500 mb-0.5">{t.name} ($/mo)</span>
                <input
                  id={`lic-price-${t.code}`}
                  type="number" min="0" step="0.01"
                  value={prices[t.code] ?? ''}
                  placeholder={d.pricing.effective_prices?.[t.code] != null ? String(d.pricing.effective_prices[t.code]) : 'plan'}
                  onChange={(e) => setPrices((p) => ({ ...p, [t.code]: e.target.value }))}
                  className="w-24 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
                />
              </label>
            ))}
            <label className="text-xs">
              <span className="block text-gray-500 mb-0.5">Bill each month on</span>
              <select id="lic-measure" value={measure} onChange={(e) => setMeasure(e.target.value)} className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-sm">
                {MEASURES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </label>
            <button onClick={savePricing} disabled={!!busy} className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:bg-gray-400">
              {busy === 'pricing' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save prices
            </button>
          </div>
        </div>
      )}

      {d.pricing.plan && (
        <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3 space-y-2">
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">Committed seats</div>
          <p className="text-xs text-gray-500">
            Committed seats bill every month even if fewer are used. Adding users is never blocked: seats above the commitment bill as a true-up
            (on annual prepay plans, on the monthly invoices). Leave blank for no commitment.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            {billableTypes.map((t) => (
              <label key={t.code} className="text-xs">
                <span className="block text-gray-500 mb-0.5">{t.name}</span>
                <input
                  id={`lic-commit-${t.code}`}
                  type="number" min="0" step="1"
                  value={commits[t.code] ?? ''}
                  placeholder="—"
                  onChange={(e) => setCommits((c) => ({ ...c, [t.code]: e.target.value }))}
                  className="w-20 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
                />
              </label>
            ))}
            <button onClick={saveCommitments} disabled={!!busy} className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:bg-gray-400">
              {busy === 'commits' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save commitments
            </button>
          </div>
          {d.over_commitment && Object.keys(d.over_commitment).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(d.over_commitment).map(([lt, n]) => (
                <span key={lt} className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                  {name(lt)}: {n} above commitment — billed as a true-up
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[680px] text-sm">
          <thead className="border-b border-gray-200 text-xs text-gray-500 dark:border-gray-700">
            <tr>
              <th className="py-2 pr-3 text-left font-medium">Member</th>
              <th className="py-2 pr-3 text-left font-medium">Role</th>
              <th className="py-2 pr-3 text-left font-medium">License</th>
              <th className="py-2 pr-3 text-left font-medium">Seat</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {d.users.map((m) => (
              <tr key={m.user_id} className={m.seat_status !== 'active' ? 'text-gray-400' : ''}>
                <td className="py-1.5 pr-3">
                  <div className="font-medium">{m.name || `User ${m.user_id}`}</div>
                  <div className="text-xs text-gray-500">{m.email}</div>
                </td>
                <td className="py-1.5 pr-3">{m.role}</td>
                <td className="py-1.5 pr-3">
                  <select
                    id={`lic-type-${m.user_id}`}
                    value={m.license_type_source === 'manual' ? m.license_type : `role:${m.license_type}`}
                    disabled={busy === `u:${m.user_id}`}
                    onChange={(e) => setMember(m, { license_type: e.target.value.startsWith('role:') ? 'role' : e.target.value })}
                    className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
                  >
                    <option value={`role:${m.license_type}`}>{name(m.license_type)} (from role)</option>
                    {types.map((t) => <option key={t.code} value={t.code}>{t.name}</option>)}
                  </select>
                </td>
                <td className="py-1.5 pr-3">
                  <select
                    id={`lic-seat-${m.user_id}`}
                    value={m.seat_status}
                    disabled={busy === `u:${m.user_id}`}
                    onChange={(e) => setMember(m, { seat_status: e.target.value })}
                    className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 py-1 text-sm"
                  >
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="released">Released</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {latestMonth && (
        <p className="text-xs text-gray-500">
          {new Date(latestMonth + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}:{' '}
          {d.snapshots.filter((s) => s.month === latestMonth && types.find((t) => t.code === s.license_type)?.billable)
            .map((s) => `${name(s.license_type)} peak ${s.peak}, avg ${s.average}`).join(' · ') || 'no billable seats recorded'}
        </p>
      )}
    </div>
  );
}
